// Folder structure:

//  /SetupWizard
//        |_______/Components
//        |             |______/Steps
//        |             |        |______Step1.js
//        |             |        |______Step2.js
//        |             |        |______Step3.js
//        |             |
//        |             |______IconSelect.js (shared element)
//        |             |______ImagePreLoader.js (imported in step 2 for preloading animation images)
//        |             |______ProfileImageUploader.js (for uploading profile image in step 1)
//        |             |______SkillFinder.js (addding skills in step 2)
//        |             |______StepIndicator.js (imported here in SetupWizard.js)
//        |             |______TermsModal.js (imported in step 1 for terms and conditions modal)
//        |
//        |_______SetupWizard.js (Current file)

// This is the Main component for user first login wizard.
// This component consists of 3 children (Step1, Step2, Step3) stored in "./Components/Steps"
// and StepIndicator displayed at the top.
// Each step has a main wrapper div with an id related to that step, all 3 steps are mounted at
// the same time but the classNames of the wrappers are different and have different display properties
// so they may not be displayed at the same time.

// NOTE :
// in this directory, we have Components that are imported and used in various places, such as
// IconSelect.js which is a reusable component imported in Step1 and Step2.

// Form submit function is in this component, and its trigger function is in Step3.js passed down as props.
// Since the main submit function is in this file, any data that is not in form of string (stored in dom elements such as select elements)
// is passed up to here from the child component and stored in component state.

import axios from "axios";
import React, { useState, useEffect } from "react";
import "../../Assets/Styles/SetupWizard.scss"; // all the styles of wizard components
import { Config } from "../../Config";
import StepIndicator from "./Components/StepIndicator";
import Step1 from "./Components/Steps/Step1";
import Step2 from "./Components/Steps/Step2";
import Step3 from "./Components/Steps/Step3";
import ConfirmationAlert from "../Dashboard/Components/ShareElements/Alert/Alert";

const SetupWizard = () => {
  const [StepNumber, setStepNumber] = useState(1); // indicates the number of the current step
  const [Languages, setLanguages] = useState(); // stores the data of all languages available in the system
  const [Skills, setSkills] = useState([]); // stores the top 5 skills entered by the user on step 2
  const [PathItems, setPathItems] = useState([]); // stores the choices of your path options on step 2
  const [SelectedLanguage, setSelectedLang] = useState();
  const [LogoutIsOpen, setLogoutIsOpen] = useState(false);

  useEffect(() => {
    checkWizardStatus();
    fetchAllLanguages("EN");
  }, []);

  // To prevent using the wizard after having done the setup, on mount, we check for the status of the user's wizard
  // if they have done it the API will return (firstWizard:false) which means the user does not need to setup the
  // wizard again. if the API returns (firstWizard:null) or (firstWizard:false) we let the user go through the steps.
  // this can also prevent manually entering the wizard by typing in the URL for wizard in the browser to setup the wizard again.
  const checkWizardStatus = () => {
    let Req = {
      method: "GET",
      baseURL:
        window._env_.API_URL + Config.AuthBaseURL + "/api/profile/first-wizard",
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
    };
    axios(Req)
      .then((Res) => {
        console.log(Res);
        if (
          Res.data.firstWizard === false ||
          Res.data.username === "superadmin"
        ) {
          window.location.assign("/");
        } else {
          return null;
        }
      })
      .catch((Err) => {
        console.log(Err);
        window.location.assign("/");
      });
  };

  const fetchAllLanguages = (StartLang) => {
    let Req = {
      method: "GET",
      baseURL:
        window._env_.API_URL +
        Config.AuthBaseURL +
        "/api/general-config/language/batch",
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
    };
    axios(Req)
      .then((Res) => {
        console.log(Res);
        setLanguages(Res.data);
        setSystemLanguage("EN", Res.data);
      })
      .catch((Err) => {
        console.log(Err);
      });
  };

  const goToStep1 = () => {
    document.getElementById("step-2").style.display = "none";
    document.getElementById("step-1").style.display = "block";
    document.getElementById("step-3").style.display = "none";
    document.getElementById("step-1").classList.add("fade-in-right");
    setTimeout(() => {
      document.getElementById("step-1").classList.remove("fade-in-right");
    }, 500);
    setStepNumber(1);
  };

  // step 2 can be accessed from both steps 1 and 3, therefore we pass the StepNumber parameter
  // to track the current step number and toggle the related classNames to display step 2.
  const goToStep2 = (StepNumber) => {
    document.getElementById("step-1").style.display = "none";
    document.getElementById("step-2").style.display = "block";
    document.getElementById("step-3").style.display = "none";
    if (StepNumber === 1) {
      document.getElementById("step-2").classList.remove("fade-in-right");
      document.getElementById("step-2").classList.add("fade-in-left");
      setTimeout(() => {
        document.getElementById("step-2").classList.remove("fade-in-left");
      }, 500);
    } else {
      document.getElementById("step-2").classList.remove("fade-in-left");
      document.getElementById("step-2").classList.add("fade-in-right");
      setTimeout(() => {
        document.getElementById("step-2").classList.remove("fade-in-right");
      }, 500);
    }
    setStepNumber(2);
  };

  // the Skills parameter is an array of selected Skills in step 2 skill finder, passed up to this
  // component as its parent to store and use on form submit.
  const goToStep3 = (Skills) => {
    setSkills(Skills);
    document.getElementById("step-1").style.display = "none";
    document.getElementById("step-2").style.display = "none";
    document.getElementById("step-3").style.display = "block";
    document.getElementById("step-3").classList.add("fade-in-left");
    setTimeout(() => {
      document.getElementById("step-3").classList.remove("fade-in-left");
    }, 500);
    setStepNumber(3);
  };

  const submitForm = () => {
    let Data = {
      countryId: document.getElementById("selected-preferred-country-input")
        .value,
      currency: document.getElementById("selected-currency-input").value,
      language: document.getElementById("selected-language-input").value,
      path: "",
      yearsOfExperience: parseInt(
        document.getElementById("yoe-single").innerHTML
      ),
    };
    if (PathItems.length === 1) {
      Data.path = PathItems[0];
    } else if (PathItems.length > 1) {
      Data.path = PathItems[0] + "," + PathItems[1];
    }
    if (
      document.getElementById("annonymous-switch-holder").classList.length === 1
    ) {
      Data.anonymous = true;
    } else {
      Data.anonymous = false;
    }
    let SkillsSelected = [];
    Skills.map((Skill) => {
      SkillsSelected.push(Skill.id);
    });
    Data.skills = SkillsSelected;
    let Req = {
      method: "POST",
      baseURL:
        window._env_.API_URL + Config.AuthBaseURL + "/api/user-info/wizard",
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
        "Content-Type": "application/json",
      },
      data: Data,
    };
    axios(Req)
      .then((Res) => {
        console.log(Res);
        if (Res.status === 201) {
          window.location.assign("/home");
        }
      })
      .catch((Err) => {
        console.log(Err.response);
      });
  };

  const applyLanguage = () => {
    setTimeout(() => {
      let Language = document.getElementById("selected-language-input").value;
      Languages.map((Lang) => {
        if (Lang.name === Language) {
          setSelectedLang(Lang);
        }
        return null;
      });
    }, 500);
  };

  const setSystemLanguage = (Language, LangData) => {
    LangData.map((Lang) => {
      if (Lang.name === Language) {
        setSelectedLang(Lang);
      }
    });
  };

  const handleLogout = () => {
    window.localStorage.removeItem("Token");
    window.location.assign("/");
  };

  return (
    <React.Fragment>
      {Languages === undefined || SelectedLanguage === undefined ? null : (
        <div className="setup-wizard">
          {LogoutIsOpen ? (
            <ConfirmationAlert
              AlertText={"Logout Account?"}
              AlertInfo={"Are you sure you want to log out?"}
              AcceptText={"Logout"}
              IsDelete={false}
              cancel={() => setLogoutIsOpen(false)}
              accept={() => handleLogout()}
            />
          ) : null}
          <StepIndicator
            CurrentStep={StepNumber}
            LangData={SelectedLanguage.data}
            ToggleLogout={() => setLogoutIsOpen(true)}
          />
          <div className="wizard-body">
            <div id="step-1">
              <Step1
                TriggerNextStep={() => goToStep2(1)}
                SetLanguage={() => applyLanguage()}
                LangData={SelectedLanguage.data}
              />
            </div>
            <div id="step-2">
              <Step2
                TriggerNextStep={(Skills) => goToStep3(Skills)}
                TriggerPrevStep={() => goToStep1()}
                SavePathItems={(PathItems) => setPathItems(PathItems)}
                LangData={SelectedLanguage.data}
                PathItems={PathItems}
              />
            </div>
            <div id="step-3">
              <Step3
                GetStarted={() => null}
                TriggerPrevStep={() => goToStep2(3)}
                SubmitForm={() => submitForm()}
                LangData={SelectedLanguage.data}
              />
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
export default SetupWizard;
